import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageSquare, Send, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { API_URL, API_BASE_URL } from "../config/api";


// Emoji faces for rating
const RATING_EMOJIS = [
  { value: 1, emoji: "😞", label: "Very Unhappy" },
  { value: 2, emoji: "😕", label: "Unhappy" },
  { value: 3, emoji: "😐", label: "Neutral" },
  { value: 4, emoji: "🙂", label: "Happy" },
  { value: 5, emoji: "😄", label: "Very Happy" },
];

const CATEGORIES = [
  { value: "usability", label: "Usability" },
  { value: "performance", label: "Performance" },
  { value: "feature_request", label: "Feature Request" },
  { value: "bug", label: "Bug Report" },
  { value: "general", label: "General Feedback" },
];

export const FeedbackWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [category, setCategory] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showTrigger, setShowTrigger] = useState(true);
  const [hasShownOnPage, setHasShownOnPage] = useState(false);
  
  const location = useLocation();
  const token = localStorage.getItem("token");

  // Reset submitted state when page changes
  useEffect(() => {
    setSubmitted(false);
    setHasShownOnPage(false);
  }, [location.pathname]);

  // Auto-show popup after some time on certain pages (optional behavior)
  useEffect(() => {
    // Don't auto-show, let user trigger it
    const timer = setTimeout(() => {
      setShowTrigger(true);
    }, 5000); // Show trigger button after 5 seconds

    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleSubmit = async () => {
    if (!rating) return;

    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(
        `${API_URL}/feedback`,
        {
          rating,
          page: location.pathname,
          comment: comment || null,
          category: category || null,
        },
        { headers }
      );

      setSubmitted(true);
      
      // Close after showing success
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        setRating(null);
        setComment("");
        setCategory("");
      }, 2000);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setRating(null);
    setComment("");
    setCategory("");
    setSubmitted(false);
  };

  // Don't show on landing page or auth pages
  const hiddenPaths = ["/", "/login", "/register"];
  if (hiddenPaths.includes(location.pathname)) {
    return null;
  }

  return (
    <>
      {/* Floating trigger button */}
      <AnimatePresence>
        {showTrigger && !isOpen && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-20 right-6 z-50 bg-primary hover:bg-primary/90 text-white p-3 rounded-full shadow-lg flex items-center gap-2 group transition-all"
            data-testid="feedback-trigger"
          >
            <MessageSquare className="w-5 h-5" />
            <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap text-sm font-medium">
              Feedback
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Feedback modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-20 right-6 z-50 w-80 bg-surface border border-border rounded-xl shadow-2xl overflow-hidden"
            data-testid="feedback-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-surface">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-primary" />
                </div>
                <span className="font-semibold text-foreground">Feedback</span>
              </div>
              <button
                onClick={handleClose}
                className="p-1 rounded-md hover:bg-secondary transition-colors"
                data-testid="feedback-close"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-6"
                >
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-6 h-6 text-emerald-500" />
                  </div>
                  <p className="text-foreground font-medium">Thank you!</p>
                  <p className="text-sm text-muted-foreground">Your feedback helps us improve</p>
                </motion.div>
              ) : (
                <>
                  {/* Rating question */}
                  <p className="text-sm text-foreground mb-3">
                    How would you rate your experience on this page?
                  </p>

                  {/* Emoji rating */}
                  <div className="flex justify-between mb-4">
                    {RATING_EMOJIS.map((item) => (
                      <button
                        key={item.value}
                        onClick={() => setRating(item.value)}
                        className={`text-2xl p-2 rounded-lg transition-all ${
                          rating === item.value
                            ? "bg-primary/20 scale-110"
                            : "hover:bg-secondary"
                        }`}
                        title={item.label}
                        data-testid={`rating-${item.value}`}
                      >
                        {item.emoji}
                      </button>
                    ))}
                  </div>

                  {/* Show additional fields after rating */}
                  <AnimatePresence>
                    {rating && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-3 overflow-hidden"
                      >
                        {/* Category */}
                        <select
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          className="w-full h-9 px-3 text-sm rounded-md border border-border bg-background text-foreground"
                          data-testid="feedback-category"
                        >
                          <option value="">Category (optional)</option>
                          {CATEGORIES.map((cat) => (
                            <option key={cat.value} value={cat.value}>
                              {cat.label}
                            </option>
                          ))}
                        </select>

                        {/* Comment */}
                        <Textarea
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          placeholder="Tell us more... (optional)"
                          rows={3}
                          className="text-sm resize-none"
                          data-testid="feedback-comment"
                        />

                        {/* Submit */}
                        <Button
                          onClick={handleSubmit}
                          disabled={submitting}
                          className="w-full gap-2"
                          size="sm"
                          data-testid="feedback-submit"
                        >
                          {submitting ? (
                            "Sending..."
                          ) : (
                            <>
                              <Send className="w-4 h-4" />
                              Submit Feedback
                            </>
                          )}
                        </Button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FeedbackWidget;
